import API from "../../services/api";
import {
  createIdempotencyKey,
  formatFinancialApiError,
  getApiErrorDetail,
  idempotencyHeaders,
  parseBankFileValidationError,
} from "../../utils/idempotency";

export {
  createIdempotencyKey,
  formatFinancialApiError,
  getApiErrorDetail,
  parseBankFileValidationError,
};

export function listSalaryBatches() {
  return API.get("/disbursement/salary-batches");
}

export function getSalaryBatch(batchId) {
  return API.get(`/disbursement/salary-batches/${batchId}`);
}

export function createSalaryBatch(data, options = {}) {
  return API.post("/disbursement/salary-batches", data, {
    headers: idempotencyHeaders("salary-batch-create", options.idempotencyKey),
  });
}

export function updateSalaryBatch(batchId, data) {
  return API.patch(`/disbursement/salary-batches/${batchId}`, data);
}

export function deleteSalaryBatch(batchId) {
  return API.delete(`/disbursement/salary-batches/${batchId}`);
}

export function approveSalaryBatchHr(batchId, body = { comment: "Approved by HR" }, options = {}) {
  return API.post(`/disbursement/salary-batches/${batchId}/approve/hr`, body, {
    headers: idempotencyHeaders("salary-batch-hr-approve", options.idempotencyKey),
  });
}

export function approveSalaryBatchFinance(
  batchId,
  body = { comment: "Approved by Finance" },
  options = {}
) {
  return API.post(`/disbursement/salary-batches/${batchId}/approve/finance`, body, {
    headers: idempotencyHeaders("salary-batch-finance-approve", options.idempotencyKey),
  });
}

export function approveSalaryBatchAdmin(
  batchId,
  body = { comment: "" },
  { override = false, idempotencyKey } = {}
) {
  return API.post(
    `/disbursement/salary-batches/${batchId}/approve/admin?override=${override ? "true" : "false"}`,
    body,
    {
      headers: idempotencyHeaders(
        override ? "salary-batch-admin-override" : "salary-batch-admin-approve",
        idempotencyKey
      ),
    }
  );
}

export function rejectSalaryBatch(batchId, body = { comment: "" }, options = {}) {
  return API.post(`/disbursement/salary-batches/${batchId}/reject`, body, {
    headers: idempotencyHeaders("salary-batch-reject", options.idempotencyKey),
  });
}

export function reopenSalaryBatch(batchId, body = { comment: "" }, options = {}) {
  return API.post(`/disbursement/salary-batches/${batchId}/reopen`, body, {
    headers: idempotencyHeaders("salary-batch-reopen", options.idempotencyKey),
  });
}

export function payoutSalaryBatch(batchId, options = {}) {
  return API.post(
    `/disbursement/salary-batches/${batchId}/payout`,
    {},
    {
      headers: idempotencyHeaders("salary-batch-payout", options.idempotencyKey),
      validateStatus: (s) => s === 200 || s === 202,
    }
  );
}

export function retryFailedSalaryBatchItems(batchId, options = {}) {
  return API.post(
    `/disbursement/salary-batches/${batchId}/retry-failed`,
    {},
    {
      headers: idempotencyHeaders("salary-batch-retry-failed", options.idempotencyKey),
      validateStatus: (s) => s === 200 || s === 202,
    }
  );
}

export function generateSalaryBatchBankFile(
  batchId,
  { bankCode, exportFormat = "csv", idempotencyKey } = {}
) {
  return API.post(
    `/disbursement/salary-batches/${batchId}/artifacts/bank-file`,
    {},
    {
      params: {
        ...(bankCode ? { bank_code: bankCode } : {}),
        ...(exportFormat ? { export_format: exportFormat } : {}),
      },
      headers: idempotencyHeaders("salary-batch-bank-file", idempotencyKey),
      validateStatus: (s) => s === 200 || s === 201,
    }
  );
}

export function listSalaryBatchArtifacts(batchId) {
  return API.get(`/disbursement/salary-batches/${batchId}/artifacts`);
}

export function getBankFileOptions() {
  return API.get("/disbursement/bank-file-options");
}

/** Fetch an artifact as a Blob. `downloadUrl` comes from listSalaryBatchArtifacts
 *  and is /api-prefixed; the axios baseURL already ends in /api. */
export function fetchArtifactBlob(downloadUrl) {
  // Prefer authenticated path without signed token — Bearer auth is enough and
  // avoids query-string token mangling blocking the download.
  const raw = String(downloadUrl || "");
  const withoutApi = raw.replace(/^\/api/, "");
  const pathOnly = withoutApi.split("?")[0];
  return API.get(pathOnly || withoutApi, { responseType: "blob" });
}

export function validateSalaryBatch(batchId, { bankCode } = {}) {
  return API.post(
    `/disbursement/salary-batches/${batchId}/validate`,
    {},
    { params: bankCode ? { bank_code: bankCode } : {} }
  );
}

export function cancelSalaryBatch(batchId, options = {}) {
  return API.post(
    `/disbursement/salary-batches/${batchId}/cancel`,
    {},
    {
      headers: idempotencyHeaders("salary-batch-cancel", options.idempotencyKey),
    }
  );
}
